import * as THREE from 'three'

export interface SplitResult {
  groups: THREE.Group[]
  componentCount: number
}

export function splitConnectedComponents(group: THREE.Group): SplitResult {
  const allMeshes: THREE.Mesh[] = []
  group.traverse(c => {
    if (c instanceof THREE.Mesh && c.geometry) allMeshes.push(c)
  })

  if (allMeshes.length === 0) return { groups: [], componentCount: 0 }
  if (allMeshes.length === 1) {
    const components = findComponents(allMeshes[0].geometry)
    if (components.length <= 1) return { groups: [group], componentCount: 1 }
    return buildGroups(allMeshes[0], components, group)
  }

  const totalComponents: { mesh: THREE.Mesh; components: number[][] }[] = []
  for (const mesh of allMeshes) {
    const comps = findComponents(mesh.geometry)
    totalComponents.push({ mesh, components: comps })
  }

  const total = totalComponents.reduce((s, c) => s + c.components.length, 0)
  if (total <= 1) return { groups: [group], componentCount: 1 }

  const newGroups: THREE.Group[] = []
  for (const { mesh, components } of totalComponents) {
    if (components.length <= 1) {
      const g = new THREE.Group()
      g.add(mesh.clone())
      copyUserData(group, g)
      newGroups.push(g)
    } else {
      for (const comp of components) {
        const cloned = cloneMeshByFaces(mesh, comp)
        const g = new THREE.Group()
        g.add(cloned)
        copyUserData(group, g)
        newGroups.push(g)
      }
    }
  }

  return { groups: newGroups, componentCount: newGroups.length }
}

function copyUserData(src: THREE.Group, dst: THREE.Group) {
  dst.userData = { ...src.userData }
}

function findComponents(geo: THREE.BufferGeometry): number[][] {
  const index = geo.index
  const posAttr = geo.attributes.position
  if (!posAttr) return [[0]]

  if (index) {
    return findComponentsIndexed(index.array as Uint16Array | Uint32Array, posAttr.count)
  } else {
    return findComponentsNonIndexed(posAttr.array as Float32Array, posAttr.count)
  }
}

function findComponentsIndexed(indices: Uint16Array | Uint32Array, vertexCount: number): number[][] {
  const faceCount = indices.length / 3
  if (faceCount === 0) return []

  const vertexFaces = new Map<number, number[]>()
  for (let f = 0; f < faceCount; f++) {
    for (let i = 0; i < 3; i++) {
      const v = indices[f * 3 + i]
      let arr = vertexFaces.get(v)
      if (!arr) { arr = []; vertexFaces.set(v, arr) }
      arr.push(f)
    }
  }

  const visited = new Uint8Array(faceCount)
  const components: number[][] = []

  for (let f = 0; f < faceCount; f++) {
    if (visited[f]) continue
    const comp: number[] = []
    const queue = [f]
    visited[f] = 1
    while (queue.length > 0) {
      const cur = queue.pop()!
      comp.push(cur)
      for (let i = 0; i < 3; i++) {
        const v = indices[cur * 3 + i]
        const faces = vertexFaces.get(v)
        if (!faces) continue
        for (const nf of faces) {
          if (!visited[nf]) {
            visited[nf] = 1
            queue.push(nf)
          }
        }
      }
    }
    components.push(comp)
  }

  return components
}

function findComponentsNonIndexed(positions: Float32Array, vertexCount: number): number[][] {
  const faceCount = vertexCount / 3
  if (faceCount === 0) return []

  const VERTEX_TOL = 1e-6
  const posMap = new Map<string, number>()
  const vertexIds: number[] = []

  for (let i = 0; i < vertexCount; i++) {
    const x = Math.round(positions[i * 3] / VERTEX_TOL) * VERTEX_TOL
    const y = Math.round(positions[i * 3 + 1] / VERTEX_TOL) * VERTEX_TOL
    const z = Math.round(positions[i * 3 + 2] / VERTEX_TOL) * VERTEX_TOL
    const key = `${x},${y},${z}`
    let id = posMap.get(key)
    if (id === undefined) {
      id = posMap.size
      posMap.set(key, id)
    }
    vertexIds.push(id)
  }

  const totalVerts = posMap.size
  const vertexFaces = new Array(totalVerts).fill(null).map(() => [] as number[])

  for (let f = 0; f < faceCount; f++) {
    const v0 = vertexIds[f * 3]
    const v1 = vertexIds[f * 3 + 1]
    const v2 = vertexIds[f * 3 + 2]
    vertexFaces[v0].push(f)
    vertexFaces[v1].push(f)
    vertexFaces[v2].push(f)
  }

  const visited = new Uint8Array(faceCount)
  const components: number[][] = []

  for (let f = 0; f < faceCount; f++) {
    if (visited[f]) continue
    const comp: number[] = []
    const queue = [f]
    visited[f] = 1
    while (queue.length > 0) {
      const cur = queue.pop()!
      comp.push(cur)
      const vi = [vertexIds[cur * 3], vertexIds[cur * 3 + 1], vertexIds[cur * 3 + 2]]
      for (const v of vi) {
        for (const nf of vertexFaces[v]) {
          if (!visited[nf]) {
            visited[nf] = 1
            queue.push(nf)
          }
        }
      }
    }
    components.push(comp)
  }

  return components
}

function buildGroups(mesh: THREE.Mesh, components: number[][], originalGroup: THREE.Group): SplitResult {
  const newGroups: THREE.Group[] = []

  if (components.length <= 1) {
    const g = new THREE.Group()
    g.add(mesh.clone())
    copyUserData(originalGroup, g)
    newGroups.push(g)
  } else {
    for (const comp of components) {
      const cloned = cloneMeshByFaces(mesh, comp)
      const g = new THREE.Group()
      g.add(cloned)
      copyUserData(originalGroup, g)
      newGroups.push(g)
    }
  }

  return { groups: newGroups, componentCount: newGroups.length }
}

function cloneMeshByFaces(source: THREE.Mesh, faceIndices: number[]): THREE.Mesh {
  const geo = source.geometry
  const index = geo.index
  const posAttr = geo.attributes.position
  const normAttr = geo.attributes.normal
  const colorAttr = geo.attributes.color
  const uvAttr = geo.attributes.uv

  const vertexMap = new Map<number, number>()
  const newPositions: number[] = []
  const newNormals: number[] = []
  const newColors: number[] = []
  const newUvs: number[] = []
  const newIndices: number[] = []
  let nextVertex = 0

  for (const fi of faceIndices) {
    for (let j = 0; j < 3; j++) {
      let vi: number
      if (index) {
        vi = index.array[fi * 3 + j] as number
      } else {
        vi = fi * 3 + j
      }

      let newVi = vertexMap.get(vi)
      if (newVi === undefined) {
        newVi = nextVertex++
        vertexMap.set(vi, newVi)

        newPositions.push(posAttr.getX(vi), posAttr.getY(vi), posAttr.getZ(vi))
        if (normAttr) newNormals.push(normAttr.getX(vi), normAttr.getY(vi), normAttr.getZ(vi))
        if (colorAttr) newColors.push(colorAttr.getX(vi), colorAttr.getY(vi), colorAttr.getZ(vi))
        if (uvAttr) newUvs.push(uvAttr.getX(vi), uvAttr.getY(vi))
      }
      newIndices.push(newVi)
    }
  }

  const newGeo = new THREE.BufferGeometry()
  newGeo.setAttribute('position', new THREE.Float32BufferAttribute(newPositions, 3))
  if (newNormals.length > 0) newGeo.setAttribute('normal', new THREE.Float32BufferAttribute(newNormals, 3))
  if (newColors.length > 0) newGeo.setAttribute('color', new THREE.Float32BufferAttribute(newColors, 3))
  if (newUvs.length > 0) newGeo.setAttribute('uv', new THREE.Float32BufferAttribute(newUvs, 2))
  newGeo.setIndex(newIndices)

  const mat = source.material instanceof THREE.Material ? source.material.clone() : source.material
  const mesh = new THREE.Mesh(newGeo, mat)
  mesh.castShadow = true
  mesh.receiveShadow = true
  return mesh
}
